const DEFAULT_PRECISION = 6;

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number.parseFloat(String(value).trim());
  return Number.isFinite(numeric) ? numeric : null;
}

function fromLatLng(lat, lng) {
  const safeLat = toFiniteNumber(lat);
  const safeLng = toFiniteNumber(lng);
  if (safeLat === null || safeLng === null) return null;
  return { lat: safeLat, lng: safeLng };
}

function fromCoordinatesArray(coords) {
  if (!Array.isArray(coords) || coords.length < 2) return null;
  return fromLatLng(coords[1], coords[0]);
}

export function normalizeGpsPoint(value) {
  if (value === null || value === undefined || value === '') return null;

  if (Array.isArray(value)) {
    return fromCoordinatesArray(value);
  }

  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return null;

    if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
      try {
        return normalizeGpsPoint(JSON.parse(text));
      } catch {
        // Fall through to the human-readable parser below.
      }
    }

    const parts = text.split(',').map((part) => Number.parseFloat(part.trim()));
    if (parts.length >= 2 && parts.every(Number.isFinite)) {
      return fromLatLng(parts[0], parts[1]);
    }

    return null;
  }

  if (typeof value !== 'object') return null;

  if (value.type === 'Point' && Array.isArray(value.coordinates)) {
    return fromCoordinatesArray(value.coordinates);
  }

  if (Array.isArray(value.coordinates)) {
    return fromCoordinatesArray(value.coordinates);
  }

  if ('lat' in value || 'lng' in value) {
    return fromLatLng(value.lat, value.lng);
  }

  if ('latitude' in value || 'longitude' in value) {
    return fromLatLng(value.latitude, value.longitude);
  }

  return null;
}

export function formatGpsCoordinates(value, precision = DEFAULT_PRECISION) {
  const point = normalizeGpsPoint(value);
  if (!point) {
    if (typeof value === 'string') return value.trim();
    return '';
  }

  return `${point.lat.toFixed(precision)}, ${point.lng.toFixed(precision)}`;
}

export function toGeoJsonPoint(value) {
  const point = normalizeGpsPoint(value);
  if (!point) return null;
  return {
    type: 'Point',
    coordinates: [point.lng, point.lat],
  };
}

export function isValidGpsPoint(value) {
  return Boolean(normalizeGpsPoint(value));
}

// ─── Waypoints (LineString) — DCIC-15 Transhumance Route gps_waypoints ────────
export function normalizeGpsWaypoints(value) {
  if (value === null || value === undefined || value === '') return [];

  if (Array.isArray(value)) {
    return value.map((point) => normalizeGpsPoint(point)).filter(Boolean);
  }

  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];

    if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
      try {
        return normalizeGpsWaypoints(JSON.parse(text));
      } catch {
        // Fall through to line-based parsing below.
      }
    }

    return text
      .split(/\r?\n/)
      .map((line) => normalizeGpsPoint(line))
      .filter(Boolean);
  }

  if (typeof value === 'object') {
    if (value.type === 'LineString' && Array.isArray(value.coordinates)) {
      return value.coordinates.map((pair) => fromCoordinatesArray(pair)).filter(Boolean);
    }
    if (Array.isArray(value.coordinates)) {
      return normalizeGpsWaypoints(value.coordinates);
    }
  }

  return [];
}

export function formatGpsWaypointsText(points = [], precision = DEFAULT_PRECISION) {
  return points.map((p) => `${p.lat.toFixed(precision)}, ${p.lng.toFixed(precision)}`).join('\n');
}

export function toGeoJsonLineString(points = []) {
  if (!Array.isArray(points) || points.length < 2) return null;
  return {
    type: 'LineString',
    coordinates: points.map((p) => [p.lng, p.lat]),
  };
}
